import { ClearButton } from "../clear-button/ClearButton.js";
import { Form, Interactable, Root as Root$1 } from "../input/Input.js";
import { Label } from "../label/Label.js";
import { Anchor, Content as Content$1, Portal, Root as Root$2, Trigger as Trigger$1 } from "../popover/Popover.js";
import { Separator as Separator$1 } from "../separator/Separator.js";
import { Tag } from "../tag/Tag.js";
import { createComboboxItemStore } from "./createComboboxItemStore.js";
import { ComboboxContext, ComboboxItemContext } from "./ComboboxContext.js";
import { COMBOBOX_SELECTION_KEYS } from "./constants.js";
import { useListboxOptionOnKeyDown } from "./useListboxOptionOnKeyDown.js";
import { twMerge } from "tailwind-merge";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Fragment as Fragment$1, use, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { faChevronDown, faChevronUp, faSearch, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { faCheck as faCheck$1 } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { pluralize } from "@givebutter/util";
import { bindForwardedRef } from "@givebutter/react-util";
import { createPortal } from "react-dom";
import Fuse from "fuse.js";
//#region src/combobox/Combobox.tsx
const ComboboxRoot = ({ value, defaultValue, onChangeValue: onChangeValueConsumer, experimentalReactiveItems = false, allowMultiple = true, allowUnselect = true, onOpenChange: onOpenChangeConsumer, resource = "item", plurality = "s", isError = false, ariaDescribedBy, disabled = false, triggerRef, popoverProps, children, ...rest }) => {
	const [selectedItems, setSelectedItems] = useState(value ?? defaultValue ?? []);
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [useLocalSearch, setUseLocalSearch] = useState(true);
	const searchRef = useRef(null);
	const listRef = useRef(null);
	const listId = useId();
	const lastClickedItemRef = useRef(null);
	const internalTriggerRef = useRef(null);
	const [searchResults, setSearchResults] = useState([]);
	const searchInstance = useMemo(() => new Fuse([], {
		keys: ["searchable"],
		threshold: .3
	}), []);
	const [experimentalItemStore] = useState(createComboboxItemStore);
	const defaultPopoverProps = useMemo(() => ({
		modal: true,
		...popoverProps
	}), [popoverProps]);
	const onChange = useCallback((items) => {
		if (!allowUnselect && items.length === 0) return;
		const lastItem = items[items.length - 1];
		const value = allowMultiple ? items : lastItem ? [lastItem] : [];
		setSelectedItems(value);
		onChangeValueConsumer?.(value);
	}, [
		onChangeValueConsumer,
		allowMultiple,
		allowUnselect
	]);
	useEffect(() => {
		if (value !== void 0) setSelectedItems(value);
	}, [value]);
	const onOpenChange = useCallback((isOpen) => {
		if (disabled) return;
		setIsOpen(isOpen);
		onOpenChangeConsumer?.(isOpen);
	}, [disabled, onOpenChangeConsumer]);
	const ctx = useMemo(() => ({
		selectedItems,
		setSelectedItems: onChange,
		isOpen,
		setIsOpen: onOpenChange,
		isError,
		ariaDescribedBy,
		query,
		setQuery,
		useLocalSearch,
		setUseLocalSearch,
		searchInstance,
		experimentalReactiveItems,
		experimentalItemStore,
		searchRef,
		listRef,
		listId,
		lastClickedItemRef,
		resource,
		plurality,
		searchResults,
		setSearchResults,
		allowMultiple,
		allowUnselect,
		disabled,
		popoverProps: defaultPopoverProps,
		triggerRef: triggerRef ?? internalTriggerRef
	}), [
		selectedItems,
		isOpen,
		query,
		useLocalSearch,
		searchInstance,
		experimentalReactiveItems,
		experimentalItemStore,
		isError,
		ariaDescribedBy,
		resource,
		plurality,
		onChange,
		lastClickedItemRef,
		onOpenChange,
		searchResults,
		setSearchResults,
		allowMultiple,
		allowUnselect,
		disabled,
		defaultPopoverProps,
		triggerRef,
		listId
	]);
	return /* @__PURE__ */ jsx(ComboboxContext, {
		value: ctx,
		...rest,
		children: /* @__PURE__ */ jsx(Root$2, {
			open: isOpen,
			onOpenChange,
			...defaultPopoverProps,
			children
		})
	});
};
const useSelectedComboboxItems = () => {
	const { selectedItems, searchInstance, experimentalReactiveItems, experimentalItemStore } = useComboboxContext();
	useSyncExternalStore(experimentalItemStore.subscribe, experimentalItemStore.getVersion, experimentalItemStore.getVersion);
	if (experimentalReactiveItems) {
		const items = experimentalItemStore.getItems();
		return selectedItems.flatMap((id) => items.get(id) ?? []);
	}
	return searchInstance.getIndex().docs.filter((doc) => selectedItems.includes(doc.id));
};
const ComboboxTrigger = ({ ref, ...props }) => {
	const { disabled, setIsOpen, ariaDescribedBy, isError, triggerRef } = useComboboxContext();
	return /* @__PURE__ */ jsx(Trigger$1, {
		ref: (node) => {
			bindForwardedRef(triggerRef, node);
			bindForwardedRef(ref, node);
		},
		role: "combobox",
		"aria-invalid": isError || void 0,
		"aria-haspopup": "listbox",
		"aria-label": "Open",
		"aria-describedby": ariaDescribedBy,
		tabIndex: 0,
		onKeyUp: (e) => {
			if (COMBOBOX_SELECTION_KEYS.includes(e.key)) setIsOpen(true);
		},
		disabled,
		asChild: true,
		...props
	});
};
const ComboboxTags = ({ children, placeholder = "Search", ...rest }) => {
	const { disabled, selectedItems, setSelectedItems, isError, allowUnselect } = useComboboxContext();
	const tags = useSelectedComboboxItems();
	return /* @__PURE__ */ jsxs(Root$1, {
		className: "cursor-pointer flex-wrap",
		isError,
		disabled,
		...rest,
		children: [
			children,
			tags.length === 0 && /* @__PURE__ */ jsx("span", {
				className: "text-gray-500",
				children: placeholder
			}),
			tags.map((tag) => {
				const canRemove = allowUnselect || selectedItems.length > 1;
				return /* @__PURE__ */ jsx(Interactable, { children: /* @__PURE__ */ jsx(Tag, {
					className: twMerge(disabled && "cursor-not-allowed opacity-70"),
					onClose: canRemove ? (e) => {
						if (disabled) return;
						e.preventDefault();
						e.stopPropagation();
						setSelectedItems(selectedItems.filter((item) => item !== tag.id));
					} : void 0,
					children: tag.label
				}) }, tag.id);
			})
		]
	});
};
const ComboboxSelect = ({ placeholder = "Select", enableDisabledStyle, className, children, ...rest }) => {
	const { disabled, isError, experimentalReactiveItems } = useComboboxContext();
	const items = useSelectedComboboxItems();
	return /* @__PURE__ */ jsx(Root$1, {
		className: twMerge("flex cursor-pointer justify-between", className),
		isError,
		disabled,
		enableDisabledStyle,
		...rest,
		children: /* @__PURE__ */ jsx(Fragment$1, { children: children ?? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ComboboxSelectValue, { placeholder }), /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: faChevronDown,
			className: "text-xs text-gray-900"
		})] }) }, experimentalReactiveItems ? void 0 : items.map((item) => item.id).join(","))
	});
};
const ComboboxSelectValue = ({ placeholder = "Select", placeholderClassName, className }) => {
	const { selectedItems, resource, plurality } = useComboboxContext();
	const items = useSelectedComboboxItems();
	const count = items.length;
	return /* @__PURE__ */ jsxs(Fragment, { children: [count === 0 && (typeof placeholder === "string" ? /* @__PURE__ */ jsx("span", {
		className: twMerge("text-gray-500", placeholderClassName),
		children: placeholder
	}) : placeholder), /* @__PURE__ */ jsxs("span", {
		className: twMerge("font-normal", className),
		children: [count === 1 && /* @__PURE__ */ jsx(Fragment, { children: items[0].label }), count > 1 && `${selectedItems.length} ${pluralize(selectedItems.length, resource, plurality)} selected`]
	})] });
};
const ComboboxPopover = ({ children, className, onPointerDownOutside: onPointerDownOutsideConsumer, ...rest }) => {
	const { isOpen, popoverProps, triggerRef } = useComboboxContext();
	const [fragment, setFragment] = useState();
	useLayoutEffect(() => {
		setFragment(new DocumentFragment());
	}, []);
	if (!isOpen) return fragment ? createPortal(children, fragment) : null;
	return /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(Content$1, {
		asChild: true,
		side: "bottom",
		sideOffset: 4,
		className: twMerge("flex w-(--radix-popover-trigger-width) flex-col gap-2 rounded-lg p-1.5", className),
		onOpenAutoFocus: popoverProps.modal ? void 0 : (e) => e.preventDefault(),
		onPointerDownOutside: (event) => {
			onPointerDownOutsideConsumer?.(event);
			const trigger = triggerRef && typeof triggerRef !== "function" ? triggerRef : null;
			const target = event.detail.originalEvent.target;
			if (target && trigger?.current?.contains(target)) event.preventDefault();
		},
		...rest,
		children: /* @__PURE__ */ jsx(ComboboxContent, { children })
	}) });
};
const ComboboxContent = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("group/combobox-content flex flex-col gap-2", className),
		...rest
	});
};
const ComboboxSearch = ({ value, onChangeValue, isLoading = false, ...restInputProps }) => {
	const inputId = useId();
	const { disabled, query, setQuery, searchRef, listRef, setUseLocalSearch, searchInstance, experimentalReactiveItems, experimentalItemStore, setSearchResults } = useComboboxContext();
	useEffect(() => {
		setQuery(value ?? "");
		setUseLocalSearch(value === void 0);
		return () => setQuery("");
	}, [
		value,
		setQuery,
		setUseLocalSearch
	]);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(Root$1, {
		className: "w-full!",
		size: "sm",
		children: [isLoading ? /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: faSpinnerThird,
			className: "size-3 animate-spin text-gray-400"
		}) : /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: faSearch,
			className: "size-3 text-gray-400"
		}), /* @__PURE__ */ jsx(Form, {
			id: inputId,
			value: query,
			onChange: (e) => {
				const searchValue = e.target.value;
				setQuery(searchValue);
				onChangeValue?.(searchValue);
				if (value === void 0) setSearchResults(experimentalReactiveItems ? experimentalItemStore.search(searchValue) : searchInstance.search(searchValue));
			},
			onKeyUp: (e) => {
				if (e.key === "ArrowDown") (listRef.current?.querySelector("[role=\"option\"]"))?.focus();
			},
			ref: searchRef,
			disabled,
			...restInputProps
		})]
	}), /* @__PURE__ */ jsx(Label, {
		htmlFor: inputId,
		className: "sr-only",
		children: "Search"
	})] });
};
const ComboboxInputTrigger = ({ value, onChangeValue, onClear, onBlur: onBlurConsumer, onKeyDown: onKeyDownConsumer, isLoading = false, ...restInputProps }) => {
	const { disabled, isError, query, isOpen, setQuery, setIsOpen, setUseLocalSearch, searchRef, listRef, listId } = useComboboxContext();
	useEffect(() => {
		setQuery(value ?? "");
		setUseLocalSearch(value === void 0);
		return () => setQuery("");
	}, [
		value,
		setQuery,
		setUseLocalSearch
	]);
	return /* @__PURE__ */ jsx(Anchor, {
		asChild: true,
		children: /* @__PURE__ */ jsxs(Root$1, {
			isError,
			disabled,
			children: [
				isLoading && /* @__PURE__ */ jsx(FontAwesomeIcon, {
					icon: faSpinnerThird,
					className: "size-3 animate-spin text-gray-400"
				}),
				/* @__PURE__ */ jsx(Form, {
					ref: searchRef,
					value: query,
					disabled,
					onChange: (e) => {
						const v = e.target.value;
						onChangeValue?.(v);
						setQuery(v);
						setIsOpen(v.length > 0);
					},
					onBlur: (e) => {
						onBlurConsumer?.(e);
						const relatedTarget = e.relatedTarget;
						if (!listRef.current?.contains(relatedTarget)) setIsOpen(false);
					},
					onKeyDown: (e) => {
						onKeyDownConsumer?.(e);
						if (e.key === "ArrowDown") {
							e.preventDefault();
							(listRef.current?.querySelector("[role=\"option\"]"))?.focus();
						}
						if (e.key === "Escape") {
							e.preventDefault();
							setIsOpen(false);
						}
					},
					role: "combobox",
					"aria-haspopup": "listbox",
					"aria-controls": listId,
					"aria-expanded": isOpen,
					"aria-autocomplete": "list",
					...restInputProps
				}),
				value && /* @__PURE__ */ jsx(Interactable, {
					className: "flex items-center",
					children: /* @__PURE__ */ jsx(ClearButton, {
						"aria-label": "Clear",
						onClick: () => {
							onClear?.();
							onChangeValue?.("");
							setQuery("");
							setIsOpen(false);
						}
					})
				})
			]
		})
	});
};
const ComboboxItems = ({ ref, children, className, ...rest }) => {
	const { query, listRef, useLocalSearch, resource, plurality, searchResults, listId } = useComboboxContext();
	const showNoneFound = !!query && useLocalSearch && searchResults.length === 0;
	return /* @__PURE__ */ jsx("div", {
		className: "relative",
		children: /* @__PURE__ */ jsxs("div", {
			role: "listbox",
			id: listId,
			"aria-label": pluralize(2, resource, plurality),
			className: twMerge("h-full max-h-[min(24rem,calc(var(--radix-popover-content-available-height)-4rem))] overflow-y-auto select-none", className),
			ref: (node) => {
				listRef.current = node;
				bindForwardedRef(ref, node);
			},
			...rest,
			children: [
				/* @__PURE__ */ jsx(ComboboxScrollUpButton, {}),
				showNoneFound && /* @__PURE__ */ jsx(ComboboxNoneFoundText, {}),
				children,
				/* @__PURE__ */ jsx(ComboboxScrollDownButton, {})
			]
		})
	});
};
const ComboboxItem = ({ ref: forwardedRef, value, children, className, disabled = false, onClick, onKeyUp, onKeyDown, onSelectItem, ...rest }) => {
	const { query, selectedItems, searchRef, searchResults, useLocalSearch, setSelectedItems, allowMultiple, allowUnselect, setIsOpen, listRef, lastClickedItemRef } = useComboboxContext();
	const isSelected = selectedItems.includes(value);
	const ref = useRef(null);
	const isVisible = useLocalSearch && query !== "" ? searchResults.some((result) => result.item.id === value) : true;
	const listboxOptionOnKeyDown = useListboxOptionOnKeyDown({
		ref,
		searchRef
	});
	const ctx = useMemo(() => ({
		isSelected,
		value,
		disabled
	}), [
		isSelected,
		disabled,
		value
	]);
	const getNextSelected = useCallback((value) => {
		if (selectedItems.includes(value)) {
			if (!allowUnselect && selectedItems.length === 1) return selectedItems;
			return selectedItems.filter((item) => item !== value);
		} else return [...selectedItems, value];
	}, [selectedItems, allowUnselect]);
	const getRangeSelected = useCallback((anchorValue, targetValue, action) => {
		const options = listRef.current?.querySelectorAll("[role=\"option\"][data-value]");
		if (!options) return null;
		const values = Array.from(options).map((el) => el.dataset.value ?? "");
		const anchorIndex = values.indexOf(anchorValue);
		const targetIndex = values.indexOf(targetValue);
		if (anchorIndex === -1 || targetIndex === -1) return null;
		const rangeValues = values.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1);
		if (action === "select") {
			const merged = /* @__PURE__ */ new Set([...selectedItems, ...rangeValues]);
			return Array.from(merged);
		} else {
			const toRemove = new Set(rangeValues);
			const filtered = selectedItems.filter((item) => !toRemove.has(item));
			if (!allowUnselect && filtered.length === 0) return selectedItems;
			return filtered;
		}
	}, [
		selectedItems,
		listRef,
		allowUnselect
	]);
	if (!isVisible) return null;
	return /* @__PURE__ */ jsx(ComboboxItemContext, {
		value: ctx,
		children: /* @__PURE__ */ jsx("div", {
			ref: (node) => {
				ref.current = node;
				bindForwardedRef(forwardedRef, node);
			},
			role: "option",
			tabIndex: 0,
			"aria-selected": isSelected,
			"aria-disabled": disabled,
			"data-disabled": disabled,
			"data-value": value,
			className: twMerge("flex items-center gap-2 rounded-md p-1.5", "hover:bg-gray-100 focus:outline-hidden focus-visible:bg-gray-100", "group/combobox-item", className),
			onKeyDown: (event) => {
				listboxOptionOnKeyDown(event);
				if (disabled) {
					event.preventDefault();
					return;
				}
				onKeyDown?.(event);
			},
			onClick: (event) => {
				if (disabled) {
					event.preventDefault();
					return;
				}
				const anchor = lastClickedItemRef.current;
				const shiftAction = selectedItems.includes(value) ? "unselect" : "select";
				const rangeResult = event.shiftKey && allowMultiple && anchor !== null ? getRangeSelected(anchor.value, value, shiftAction) : null;
				if (rangeResult === null) {
					const nextSelectedItems = getNextSelected(value);
					const action = selectedItems.includes(value) ? "unselect" : "select";
					onClick?.(event);
					onSelectItem?.({
						event,
						selectedItem: value,
						selectedItems: nextSelectedItems
					});
					if (!event.defaultPrevented) {
						setSelectedItems(nextSelectedItems);
						lastClickedItemRef.current = {
							value,
							action
						};
					}
					if (!allowMultiple && nextSelectedItems.length > 0) setIsOpen(false);
				} else {
					onClick?.(event);
					onSelectItem?.({
						event,
						selectedItem: value,
						selectedItems: rangeResult
					});
					if (!event.defaultPrevented) {
						setSelectedItems(rangeResult);
						lastClickedItemRef.current = {
							value,
							action: shiftAction
						};
					}
				}
			},
			onKeyUp: (event) => {
				if (disabled) {
					event.preventDefault();
					return;
				}
				onKeyUp?.(event);
				if (COMBOBOX_SELECTION_KEYS.includes(event.key)) {
					const nextSelectedItems = getNextSelected(value);
					onSelectItem?.({
						event,
						selectedItem: value,
						selectedItems: nextSelectedItems
					});
					if (!event.defaultPrevented) setSelectedItems(nextSelectedItems);
					if (!allowMultiple && nextSelectedItems.length > 0) setIsOpen(false);
				}
			},
			...rest,
			children
		})
	});
};
const ComboboxItemText = ({ ref: forwardedRef, ...props }) => {
	const ref = useRef(null);
	const { searchInstance, experimentalReactiveItems, experimentalItemStore } = useComboboxContext();
	const { value } = use(ComboboxItemContext);
	useLayoutEffect(() => {
		if (experimentalReactiveItems) {
			experimentalItemStore.upsert({
				id: value,
				searchable: ref.current?.innerText ?? "",
				label: props.children
			});
			return;
		}
		if (!searchInstance.getIndex().docs.some((d) => d.id === value)) searchInstance.add({
			id: value,
			searchable: ref.current?.innerText ?? "",
			label: props.children
		});
	}, [
		value,
		searchInstance,
		experimentalReactiveItems,
		experimentalItemStore,
		props.children
	]);
	return /* @__PURE__ */ jsx("span", {
		ref: (node) => {
			ref.current = node;
			bindForwardedRef(forwardedRef, node);
		},
		...props
	});
};
const ComboboxScrollUpButton = (props) => {
	const { listRef, searchResults } = useComboboxContext();
	const [canScrollUp, setCanScrollUp] = useState(false);
	useEffect(() => {
		if (!listRef.current) return;
		const container = listRef.current;
		function handleScroll() {
			const canScrollUp = (container?.scrollTop ?? 0) > 0;
			setCanScrollUp(canScrollUp);
		}
		handleScroll();
		container?.addEventListener("scroll", handleScroll);
		return () => container?.removeEventListener("scroll", handleScroll);
	}, [
		listRef,
		listRef.current?.scrollHeight,
		searchResults
	]);
	return canScrollUp ? /* @__PURE__ */ jsx(ComboboxScrollButton, {
		className: "absolute top-0 right-0 left-0 flex cursor-default items-center justify-center bg-linear-to-b from-white to-transparent py-1",
		direction: "up",
		...props,
		children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: faChevronUp,
			className: "size-3"
		})
	}) : null;
};
const ComboboxScrollDownButton = (props) => {
	const { listRef, searchResults } = useComboboxContext();
	const [canScrollDown, setCanScrollDown] = useState(false);
	useEffect(() => {
		if (!listRef.current) return;
		const container = listRef.current;
		function handleScroll() {
			const maxScroll = container.scrollHeight - container.clientHeight;
			const canScrollDown = Math.ceil(container.scrollTop) < maxScroll;
			setCanScrollDown(canScrollDown);
		}
		handleScroll();
		container?.addEventListener("scroll", handleScroll);
		return () => container?.removeEventListener("scroll", handleScroll);
	}, [
		listRef,
		listRef.current?.scrollHeight,
		searchResults
	]);
	return canScrollDown ? /* @__PURE__ */ jsx(ComboboxScrollButton, {
		className: "absolute right-0 bottom-0 left-0 flex cursor-default items-center justify-center bg-linear-to-t from-white to-transparent py-1",
		direction: "down",
		...props,
		children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: faChevronDown,
			className: "size-3"
		})
	}) : null;
};
const AUTOSCROLL_DISTANCE = 10;
const ComboboxScrollButton = ({ direction, ...rest }) => {
	const { listRef } = useComboboxContext();
	const autoScrollTimerRef = useRef(null);
	const clearAutoScrollTimer = useCallback(() => {
		if (autoScrollTimerRef.current !== null) {
			window.clearInterval(autoScrollTimerRef.current);
			autoScrollTimerRef.current = null;
		}
	}, []);
	useEffect(() => {
		return () => clearAutoScrollTimer();
	});
	const onAutoScroll = useCallback(() => {
		if (direction === "up") listRef.current?.scrollTo({ top: listRef.current?.scrollTop - AUTOSCROLL_DISTANCE });
		else listRef.current?.scrollTo({ top: listRef.current?.scrollTop + AUTOSCROLL_DISTANCE });
	}, [listRef, direction]);
	return /* @__PURE__ */ jsx("div", {
		onPointerEnter: () => {
			if (autoScrollTimerRef.current === null) autoScrollTimerRef.current = window.setInterval(onAutoScroll, 50);
		},
		onPointerLeave: () => {
			clearAutoScrollTimer();
		},
		...rest
	});
};
const ComboboxNoneFoundText = ({ children, className, ...rest }) => {
	const { resource, plurality } = useComboboxContext();
	const text = `No ${pluralize(0, resource, plurality)} found`;
	return /* @__PURE__ */ jsx("span", {
		role: "option",
		"aria-disabled": true,
		"aria-selected": false,
		className: twMerge("text-medium block p-1.5", className),
		...rest,
		children: children ?? text
	});
};
const ComboboxCheckboxIndicator = () => {
	const { isSelected } = use(ComboboxItemContext);
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("aspect-square h-4 rounded-sm p-0.5", !isSelected && "border border-gray-300 bg-white", isSelected && "border-transparent bg-blue-500"),
		children: /* @__PURE__ */ jsx("div", {
			className: "flex flex-col items-center justify-center text-white",
			children: isSelected === true && /* @__PURE__ */ jsx(FontAwesomeIcon, {
				icon: faCheck$1,
				size: "xs"
			})
		})
	});
};
const ComboboxCheckmarkIndicator = () => {
	const { isSelected } = use(ComboboxItemContext);
	return isSelected ? /* @__PURE__ */ jsx(FontAwesomeIcon, {
		icon: faCheck$1,
		className: "ml-auto"
	}) : null;
};
const ComboboxSeparator = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(Separator$1, {
		"aria-hidden": true,
		className: twMerge("my-1", className),
		...rest
	});
};
const Root = ComboboxRoot;
const Trigger = ComboboxTrigger;
const Tags = ComboboxTags;
const Select = ComboboxSelect;
const SelectValue = ComboboxSelectValue;
const Popover = ComboboxPopover;
const Content = ComboboxContent;
const Search = ComboboxSearch;
const InputTrigger = ComboboxInputTrigger;
const Items = ComboboxItems;
const Item = ComboboxItem;
const ItemText = ComboboxItemText;
const NoneFoundText = ComboboxNoneFoundText;
const CheckmarkIndicator = ComboboxCheckmarkIndicator;
const CheckboxIndicator = ComboboxCheckboxIndicator;
const Separator = ComboboxSeparator;
const useComboboxContext = () => use(ComboboxContext);
//#endregion
export { CheckboxIndicator, CheckmarkIndicator, ComboboxCheckboxIndicator, ComboboxCheckmarkIndicator, ComboboxContent, ComboboxInputTrigger, ComboboxItem, ComboboxItemText, ComboboxItems, ComboboxNoneFoundText, ComboboxPopover, ComboboxRoot, ComboboxSearch, ComboboxSelect, ComboboxSelectValue, ComboboxSeparator, ComboboxTags, ComboboxTrigger, Content, InputTrigger, Item, ItemText, Items, NoneFoundText, Popover, Root, Search, Select, SelectValue, Separator, Tags, Trigger, useComboboxContext };
